import React, { memo, useMemo } from "react";
import { SR_ONLY } from "../constants/ui";

const GEO_TONES = {
  include: { label: "Included", fg: "#323084", bg: "#EEF2FF", toggleLabel: "Exclude" },
  exclude: { label: "Excluded", fg: "#e53935", bg: "#FFF5F5", toggleLabel: "Include" },
};


// The bidder treats a geo without an explicit "exclude" as an include (campaign_matcher.py).
const clusivityOf = (geo) => (geo.clusivity === "exclude" ? "exclude" : "include");

// Stable identity of one targeted geo. Legacy synced rows can lack an `id`, so fall back to its
// name rather than letting every id-less geo collapse into the same key.
export const geoKey = (geo) => `${clusivityOf(geo)}:${geo.id ?? geo.display_name ?? geo.name}`;

// One clusivity's chips. Advertisers upload CSVs with hundreds of DMAs or zip codes, so the
// list caps its height and scrolls inside itself rather than pushing the rest of its container
// off-screen.
function GeoGroup({ geos, clusivity, onRemove, onToggle, onDragStart, onDragEnd, labelPrefix, markedKeys, markLabel }) {
  if (geos.length === 0) return null;
  const tone = GEO_TONES[clusivity];
  const listLabel = labelPrefix ? `${labelPrefix} ${tone.label.toLowerCase()} geos` : `${tone.label} geos`;
  return (
    <div style={{ marginBottom:10, minWidth:0 }}>
      <div style={{ fontSize:10, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:4, fontVariantNumeric:"tabular-nums" }}>
        {tone.label} ({geos.length})
      </div>
      <ul
        aria-label={listLabel}
        style={{ display:"flex", flexWrap:"wrap", gap:6, margin:0, padding:6, listStyle:"none", minWidth:0, maxHeight:148, overflowY:"auto", overflowX:"hidden", overscrollBehavior:"contain", scrollbarWidth:"thin", scrollbarColor:"#CFCFCF transparent", border:"1px solid #F0F0F0", borderRadius:8, background:"#FAFAFA" }}
      >
        {geos.map((geo, i) => {
          const name = geo.display_name || geo.name;
          const key = geoKey(geo);
          const marked = markedKeys?.has(key);
          return (
            <li
              key={geo.id ?? `${key}-${i}`}
              draggable={onDragStart ? true : undefined}
              onDragStart={onDragStart && (e => {
                // Firefox starts a drag only once some data is set. A custom type, not text, so
                // a text field the chip is dropped on does not paste its name.
                e.dataTransfer?.setData("application/x-audiohook-geo", name);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                onDragStart(geo);
              })}
              onDragEnd={onDragEnd}
              style={{ cursor: onDragStart ? "grab" : undefined, position:"relative", display:"inline-flex", alignItems:"center", gap:4, maxWidth:"100%", minWidth:0, overflowWrap:"anywhere", padding:"4px 8px", background:tone.bg, color:tone.fg, borderRadius:6, fontSize:12, boxShadow: marked ? "0 0 0 1.5px #F90091" : "none", fontWeight: marked ? 600 : 400 }}>
              {name}
              {marked && <span style={SR_ONLY}>({markLabel})</span>}
              {onRemove && (
                <button type="button" onClick={() => onRemove(geo)} style={{ background:"none", border:"none", color:tone.fg, cursor:"pointer", padding:0, lineHeight:1, fontSize:14 }} aria-label={`Remove ${name}`}>×</button>
              )}
              {onToggle && (
                <button type="button" onClick={() => onToggle(geo)} title={tone.toggleLabel} style={{ background:"none", border:"none", color:"#7b7b7b", cursor:"pointer", padding:0, fontSize:10, textTransform:"lowercase" }} aria-label={`${tone.toggleLabel} ${name}`}>{tone.toggleLabel}</button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Every selected geo, included first then excluded.
// - onRemove(geo) / onToggle(geo): pass while editing; omit for a read-only list. They receive
//   the geo object itself, since legacy geos can lack an id.
// - labelPrefix: distinguishes two lists on one screen ("Previous" → "Previous included geos").
// - markedKeys (Set of geoKey) + markLabel: highlight specific chips, e.g. what a diff added.
// - onDragStart(geo) / onDragEnd(): make the chips draggable (moving a geo between two lists).
// Memoized: a drawer re-render (a keystroke elsewhere) must not re-render hundreds of chips,
// so callers pass stable handlers.
export const SelectedGeoGroups = memo(function SelectedGeoGroups({ geos, ...rest }) {
  const groups = useMemo(() => {
    const byClusivity = { include: [], exclude: [] };
    for (const g of geos) byClusivity[clusivityOf(g)].push(g);
    return byClusivity;
  }, [geos]);
  return ["include", "exclude"].map(clusivity => (
    <GeoGroup key={clusivity} clusivity={clusivity} geos={groups[clusivity]} {...rest} />
  ));
});
